import React, { Fragment, type ReactElement, type ReactNode } from 'react';

import { ColumnHeaderCell, type SortDirection } from '../ColumnFilter';
import { useUIColors } from '../../theme';
import { cx, defineRecipe, useRecipe, type Styleable } from '../../variants';

/**
 * EAA-1821: the generic half of a hand-built `<table>` — its own scroll
 * region, an optional sticky header built from `ColumnHeaderCell`, and rows
 * that can be a plain grid or an activatable list — pulled out of the
 * console's audit table so the next screen that cannot use `DataTable` does
 * not re-hand-roll the same scaffolding.
 *
 * Reach for `DataTable` first — it covers sorting, filtering, pagination and
 * row selection over antd's `Table` and is the shorter path for an ordinary
 * list. Reach for `PlainTable` only when a screen needs something antd's
 * column model cannot express: a column that swaps identity per row (the
 * audit table's "Chế độ"/"Đối tượng" split), a domain row tint keyed off data
 * antd never sees, or a filter popover anchored inside the scroll region
 * rather than antd's own portal layer. `PlainTable` renders the markup; it
 * owns no filter state, no domain vocabulary and no copy — everything a
 * caller sees comes in through `columns`/`rows`/`labels`.
 *
 * EAA-1804: `sections` (see `PlainTableSection`) adds the one thing plain
 * `columns`/`rows`/`render` still could not express — a labelled, full-width
 * divider row interleaved between groups of data rows. Modelled on
 * `MatrixTable`'s `sections` so the two kit tables describe "a band of rows
 * under a header" the same way.
 *
 * What stayed in `apps/portal/src/pages/console/audit/AuditTable.tsx` and did
 * NOT move here, because it is specific to that screen and not to tables in
 * general: which columns exist, the credential-mode/target-type column swap,
 * the failed-row tint, the per-cell renderers (actor/target/date/status), and
 * all Vietnamese copy. Those stay app-side and are expressed through this
 * component's `columns`/`rowClassName`/`render` props.
 */

/** One column: a header slot the caller may make sortable and/or give a
 *  filter control, and a `render` that owns the cell body for every row. */
export interface PlainTableColumn<T> {
    key: string;
    label: ReactNode;
    /** CSS width, e.g. `'17%'` — fed into the table's `<colgroup>`. */
    width?: string;
    /** Makes the header label a sort toggle; the click still goes through `sort.onSort`. */
    sortable?: boolean;
    /** The column's own filter control — typically a `ColumnFilterButton` inside a `FilterPopover`. */
    control?: ReactNode;
    render: (row: T) => ReactNode;
    /** Per-cell class hook, so an app can style one column without a descendant selector. */
    cellClassName?: string;
}

/** Which column is sorted, which way, and how the header asks for a change —
 *  grouped so the sort axis stays one prop instead of three. */
export interface PlainTableSort {
    key: string;
    direction: SortDirection;
    onSort: (key: string) => void;
}

/**
 * EAA-1804 (kit gap): an interleaved group-header row spanning every column,
 * with the rows it groups nested under it — the shape `MatrixTable`'s
 * `MatrixSection` already uses, named the same way so the two kit tables
 * share one vocabulary for "a labelled band of rows" instead of inventing a
 * second one here. Two hand-rolled `apps/portal` tables were blocked on this:
 * `UserGroupsTab` needs a "CÓ VAI TRÒ"/"CHƯA GÁN VAI TRÒ" divider between
 * groups, and `RolesTab` needs the same divider between its role sections —
 * both already fit `PlainTable`'s per-column `render` for everything else.
 */
export interface PlainTableSection<T> {
    key: string;
    /** Band label, e.g. "CÓ VAI TRÒ". Caller-supplied — no domain copy lives here. */
    label: ReactNode;
    /** Right of the label, e.g. a count. */
    meta?: ReactNode;
    rows: T[];
}

export interface PlainTableProps<T> extends Styleable {
    columns: PlainTableColumn<T>[];
    rows: T[];
    rowKey: (row: T) => string;
    sort?: PlainTableSort;
    /**
     * Groups `rows` under labelled, full-width header rows. ADDITIVE: when
     * omitted, `rows` renders exactly as it always has (this prop introduces
     * no new default and touches no branch of the flat-row path). When
     * present, it takes over the body entirely — `rows` is ignored, so a
     * caller using `sections` passes `rows={[]}` and puts its data in
     * `sections[].rows` instead. Kept as two props rather than a union so an
     * existing caller's `rows`-only call site never has to change shape.
     *
     * Sort interaction: `PlainTable` never reorders data in EITHER mode — the
     * `sort` prop only drives the header's indicator/click, and the caller
     * is always the one who orders `rows`/`sections[].rows` (see
     * `PlainTableSort`'s existing contract). With `sections` set, that means
     * sorting is scoped PER SECTION: each section's own `rows` keep whatever
     * order the caller gave them, and sections themselves keep their given
     * order too. Nothing here silently flattens or reshuffles sections out
     * from under a sort — a caller that wants sort-across-sections has to
     * produce that order itself, same as it already produces row order today.
     */
    sections?: PlainTableSection<T>[];
    /** Row click + Enter/Space. Absent ⇒ rows render as plain, non-interactive `<tr>`s. */
    onRowActivate?: (row: T) => void;
    /** App-supplied per-row class — how a caller expresses a row tint (e.g. a
     *  failed-row highlight) WITHOUT this package learning the domain state
     *  behind it. */
    rowClassName?: (row: T) => string | undefined;
    /** Sticky header inside THIS component's own scroll region — see the note below. */
    stickyHeader?: boolean;
    /** Rendered as a single full-width row in place of the body when `rows`
     *  (or, in sections mode, `sections`) is empty. */
    empty?: ReactNode;
}

// EAA-1821: sticky lives on the SCROLL CONTAINER, not the table — `overflow`
// on an ancestor is exactly what breaks `position: sticky`, so a caller that
// wraps this component in its own scrolling div gets a header that scrolls
// away with the rows. `stickyHeader` therefore only works because this
// component owns the scroll region itself; it is not decorative.
export const plainTableScrollRecipe = defineRecipe('plainTableScroll', 'ui-plain-table-scroll', {
    variants: {
        sticky: { yes: 'ui-plain-table-scroll--sticky', no: '' },
    },
    defaultVariants: { sticky: 'no' },
});

// String `yes`/`no` keys, not booleans — see CLAUDE.md: a boolean-keyed axis
// cannot be addressed by the string-keyed override registry.
export const plainTableRowRecipe = defineRecipe('plainTableRow', 'ui-plain-table-row', {
    variants: {
        interactive: { yes: 'ui-plain-table-row--interactive', no: '' },
    },
    defaultVariants: { interactive: 'no' },
});

export function PlainTable<T>({
    columns,
    rows,
    rowKey,
    sort,
    sections,
    onRowActivate,
    rowClassName,
    stickyHeader = false,
    empty,
    className,
    style,
}: PlainTableProps<T>): ReactElement {
    const colors = useUIColors();
    const scrollClassName = useRecipe(plainTableScrollRecipe, {
        sticky: stickyHeader ? 'yes' : 'no',
    });
    // EAA-1821: resolved once per render, above the `rows.length === 0` early
    // exit further down — `useRecipe` is a hook and must run unconditionally.
    const interactiveRowClassName = useRecipe(plainTableRowRecipe, {
        interactive: onRowActivate ? 'yes' : 'no',
    });

    function handleKeyDown(event: React.KeyboardEvent<HTMLTableRowElement>, row: T): void {
        if (!onRowActivate) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Space must not scroll the page while a row has focus.
        event.preventDefault();
        onRowActivate(row);
    }

    // EAA-1804: pulled out unchanged so both the flat-`rows` path and the new
    // `sections` path render the exact same `<tr>` for a data row — the
    // no-`sections` branch below calls this the same way it inlined the map
    // before, so its markup is untouched.
    function renderRow(row: T): ReactElement {
        const interactive = Boolean(onRowActivate);
        return (
            <tr
                key={rowKey(row)}
                className={cx(interactiveRowClassName, rowClassName?.(row))}
                // EAA-1821: only an activatable row gets `tabIndex`/`role` —
                // a non-interactive table must not announce itself as one to
                // assistive tech or trap Tab on rows nothing happens to.
                role={interactive ? 'button' : undefined}
                tabIndex={interactive ? 0 : undefined}
                style={{ cursor: interactive ? 'pointer' : undefined }}
                onClick={interactive ? () => onRowActivate?.(row) : undefined}
                onKeyDown={interactive ? (event) => handleKeyDown(event, row) : undefined}
            >
                {columns.map((column) => (
                    <td
                        key={column.key}
                        className={column.cellClassName}
                        style={{ borderBottom: `1px solid ${colors.divider}` }}
                    >
                        {column.render(row)}
                    </td>
                ))}
            </tr>
        );
    }

    return (
        <div
            className={cx('ui-plain-table-scroll', scrollClassName, className)}
            style={{ overflow: 'auto', ...style }}
        >
            <table
                className="ui-plain-table"
                style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
            >
                {columns.some((column) => column.width) && (
                    <colgroup>
                        {columns.map((column) => (
                            <col key={column.key} style={{ width: column.width }} />
                        ))}
                    </colgroup>
                )}
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <ColumnHeaderCell
                                key={column.key}
                                label={column.label}
                                control={column.control}
                                sticky={stickyHeader}
                                sorted={
                                    column.sortable && sort?.key === column.key
                                        ? sort.direction
                                        : null
                                }
                                onSort={
                                    column.sortable && sort
                                        ? () => sort.onSort(column.key)
                                        : undefined
                                }
                            />
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sections ? (
                        sections.length === 0 && empty ? (
                            <tr>
                                <td colSpan={columns.length}>{empty}</td>
                            </tr>
                        ) : (
                            sections.map((section) => (
                                <Fragment key={section.key}>
                                    <tr className="ui-plain-table-section">
                                        {/*
                                            EAA-1804: `scope="rowgroup"` (not `colgroup`) — this
                                            cell spans every COLUMN of its own row, but what it
                                            HEADS is the group of ROWS that follows it in the
                                            body, exactly the "heading for a following row group"
                                            case `scope="rowgroup"` names. `colgroup` would claim
                                            it labels a group of columns, which is backwards here.
                                        */}
                                        <th
                                            scope="rowgroup"
                                            className="ui-plain-table-section-header"
                                            colSpan={columns.length}
                                            style={{ borderBottom: `1px solid ${colors.divider}` }}
                                        >
                                            {section.label}
                                            {section.meta !== undefined && (
                                                <span className="ui-plain-table-section-meta">
                                                    {section.meta}
                                                </span>
                                            )}
                                        </th>
                                    </tr>
                                    {section.rows.map((row) => renderRow(row))}
                                </Fragment>
                            ))
                        )
                    ) : rows.length === 0 && empty ? (
                        <tr>
                            <td colSpan={columns.length}>{empty}</td>
                        </tr>
                    ) : (
                        rows.map((row) => renderRow(row))
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default PlainTable;
